/**
 * Fokus Clipboard Constants & Shared Helpers
 * 
 * v2 Schema Design:
 * - Omit null/undefined/empty values
 * - Omit default values (nodeType: 'task', false booleans)
 * - Use array indices for dependencies instead of IDs
 * - Embed position directly in task as [x, y] tuple
 * - No copiedAt timestamp
 */

export const FOKUS_CLIPBOARD_VERSION = 'v2';

/**
 * Generate a unique ID with timestamp and random suffix
 */
export function generateId(prefix, timestamp, ...indices) {
  const indexPart = indices.length > 0 ? `-${indices.join('-')}` : '';
  return `${prefix}-${timestamp}${indexPart}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Helper to omit undefined/null/empty values from an object
 */
export function omitEmpty(obj) {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) continue;
    result[key] = value;
  }
  return result;
}

/**
 * Clean icon object for clipboard - remove internal fields like _inheritedFromTag
 */
export function cleanIcon(icon) {
  if (!icon) return undefined;
  return omitEmpty({
    type: icon.type,
    value: icon.value,
    color: icon.color,
  });
}

/**
 * Build tags array with primary tag first
 * Merges category (legacy) with tags array, ensuring primary tag is first
 */
export function buildTagsArray(task) {
  const tags = Array.isArray(task.tags) ? [...task.tags] : [];
  
  // If there's a category and it's not already the first tag, ensure it is
  if (task.category) {
    const categoryIndex = tags.indexOf(task.category);
    if (categoryIndex === -1) {
      // Category not in tags, add it as first (primary)
      tags.unshift(task.category);
    } else if (categoryIndex > 0) {
      // Category exists but not first, move it to first position
      tags.splice(categoryIndex, 1);
      tags.unshift(task.category);
    }
    // If categoryIndex === 0, it's already first, do nothing
  }
  
  return tags.length > 0 ? tags : undefined;
}

/**
 * Check if clipboard data is valid Fokus format
 */
export function isValidFokusClipboard(data) {
  return data && 
         data.fokus === FOKUS_CLIPBOARD_VERSION && 
         Array.isArray(data.tasks) && 
         data.tasks.length > 0;
}
